import { useState } from "react";
import { promptInput, type Prompt, type PromptInput, type PromptUpdate } from "@akhshy/types";
import type { UseResource } from "../../../lib/useResource.ts";
import { useForm } from "../../../lib/useForm.ts";
import { useSlugField } from "../../../lib/slug.ts";
import { FormField } from "../../molecules/FormField.tsx";
import { MetaFields } from "../../molecules/MetaFields.tsx";
import { MarkdownField } from "../../molecules/MarkdownField.tsx";
import { SlugField } from "../../molecules/SlugField.tsx";
import { TagInput } from "../../molecules/TagInput.tsx";
import { FormActions } from "../../molecules/FormActions.tsx";
import { Input } from "../../atoms/Input.tsx";

interface Props {
  initial: Prompt | null;
  onDone: () => void;
  resource: UseResource<Prompt, PromptInput, PromptUpdate>;
}

export function PromptForm({ initial, onDone, resource }: Props) {
  const { errors, submitting, submit } = useForm(promptInput, "Prompt saved");
  const [useCase, setUseCase] = useState(initial?.useCase ?? "");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const { slug, setSlug, suggestFrom } = useSlugField(initial?.slug ?? "");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [published, setPublished] = useState(initial?.published ?? true);
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? 0);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit(
      { useCase, title, body, slug: slug || undefined, tags, published, sortOrder },
      async (parsed) => {
        if (initial) await resource.update(initial.id, parsed);
        else await resource.create(parsed);
        onDone();
      },
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <FormField label="Use case" error={errors["useCase"]}>
        <Input value={useCase} placeholder="Code review" onChange={(e) => setUseCase(e.target.value)} />
      </FormField>
      <FormField label="Title" error={errors["title"]}>
        <Input
          value={title}
          placeholder="The skeptical reviewer"
          onChange={(e) => {
            setTitle(e.target.value);
            suggestFrom(e.target.value);
          }}
        />
      </FormField>
      <SlugField slug={slug} onSlug={setSlug} error={errors["slug"]} />
      <MarkdownField label="Prompt body" value={body} onChange={setBody} rows={12} error={errors["body"]} />
      <TagInput value={tags} onChange={setTags} error={errors["tags"]} />
      <MetaFields
        published={published}
        sortOrder={sortOrder}
        onPublished={setPublished}
        onSortOrder={setSortOrder}
        error={errors["sortOrder"]}
      />
      <FormActions submitting={submitting} editing={initial !== null} onCancel={onDone} />
    </form>
  );
}
